/**
 * The complete 7(a) prequalification estimate.
 *
 * This is the top of the calculator: an owner says what they are buying and how
 * much of their own money is going in, and gets back everything they need to
 * walk into a lender's office — term, payment, cash at closing, what happens if
 * rates move, and what the whole thing costs.
 *
 * Nothing here does arithmetic of its own. It composes the tested modules:
 * `maxMaturity` for the term, `upfrontFee` for the guaranty fee, `cashToClose`
 * for the closing figure, `amortize` for the schedule. Its own job is to put
 * them in the right order and be honest about which numbers are published and
 * which are estimates.
 *
 * The rate is the one genuinely uncertain input. A lender sets it, within SBA's
 * ceiling. When the owner has a quote we use it exactly; otherwise we estimate
 * from what comparable SBA loans actually priced at and label it as an estimate.
 */

import primeJson from '../../src/data/prime.json';
import modelJson from '../../src/data/model.json';
import { rateCeilingPct, round2 } from '../../src/core/rates.js';
import { sizeBucketFor } from '../../src/core/score.js';
import { amortize } from './annuity.js';
import { cashToClose } from './cashToClose.js';
import { checkExposure } from './fee7a.js';
import { maxMaturity } from './maturity.js';
import type { Allocation, MaturityResult } from './maturity.js';
import type { Amortization, Citation, FeeResult, LoanType, PriorLoan } from './types.js';
import type { CashToCloseResult } from './cashToClose.js';

const PRIME = primeJson as { pct: number; asOf: string; source: string; citation: string };
const SPREADS = (modelJson as unknown as {
  rateSpreads: {
    global: { n: number; p10: number; p50: number; p90: number };
    bySize: Record<string, { n: number; p10: number; p50: number; p90: number } | undefined>;
  };
  meta?: { asOf?: string };
}).rateSpreads;

const MONEY = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export interface Loan7aInput {
  /** What the money is for. The amounts are USES, i.e. total project cost. */
  allocations: Allocation[];
  /** The owner's own money going in. The loan is the rest. */
  equityInjection?: number;

  // --- shape of the deal
  isChangeOfOwnership?: boolean;
  businessPurchasePrice?: number;
  closeRelationshipBuyerSeller?: boolean;
  specialPurposeProperty?: boolean;
  isFarm?: boolean;
  equipmentUsefulLifeYears?: number;
  installationMonths?: number;
  constructionMonths?: number;

  // --- program
  loanType?: LoanType;
  /** Defaults to today. Picks the fee schedule. */
  approvalDate?: string;
  naicsSector?: string;
  isMarc?: boolean;
  isVeteranOwned?: boolean;
  priorWithin90Days?: PriorLoan[];
  otherGuaranteed7a?: number;
  existing504Guaranteed?: number;

  // --- terms
  /** The owner's quoted rate. Omit to have one estimated. */
  ratePct?: number;
  rateType?: 'variable' | 'fixed';
  /** A term shorter than the maximum. Omit to use the maximum SBA allows. */
  termMonths?: number;
  interestOnlyMonths?: number;

  // --- closing
  financeGuarantyFee?: boolean;
  costOverrides?: Record<string, number>;
}

export interface RateEstimate {
  /** The rate used for every figure. Never above SBA's ceiling. */
  pct: number;
  /** What the owner was quoted, when that differed from `pct`. */
  quotedPct?: number;
  /** Prime, or whatever base was used. */
  basePct: number;
  baseAsOf: string;
  /** Points over the base. */
  spreadPct: number;
  /** The most SBA lets a lender charge on a loan this size. */
  ceilingPct: number;
  type: 'variable' | 'fixed';
  /** False when the owner gave us their quoted rate. */
  isEstimate: boolean;
  /** Plausible low and high, when estimated. */
  lowPct: number | null;
  highPct: number | null;
  /**
   * True when a quoted rate is above the most SBA permits on a loan this size.
   * Worth telling the owner: it means the quote is wrong, or the loan is not
   * the 7(a) product they think it is.
   */
  aboveCeiling: boolean;
  citation: Citation;
}

export interface RateRiseRow {
  label: string;
  ratePct: number;
  monthlyPayment: number;
  extraPerMonth: number;
  extraOverTerm: number;
  /** True when this rate would breach SBA's ceiling, so it cannot happen. */
  aboveCeiling: boolean;
}

export interface Loan7aEstimate {
  projectCost: number;
  equityInjection: number;
  loanAmount: number;
  /** Loan plus the guaranty fee, when financed. What is actually amortised. */
  amountBorrowed: number;

  maturity: MaturityResult;
  termMonths: number;
  termYears: number;

  rate: RateEstimate;
  schedule: Amortization;
  monthlyPayment: number;
  interestOnlyPayment: number | null;

  closing: CashToCloseResult;

  /**
   * SBA's annual fee on the guaranteed balance. Surfaced deliberately and kept
   * out of every borrower figure: the lender pays it and may not pass it on.
   */
  lenderAnnualFee: FeeResult['lenderAnnualFee'];

  totalInterest: number;
  totalCost: number;

  rateRise: RateRiseRow[] | null;
  exposure: { ok: boolean; violations: string[] };
  notes: string[];
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * A rate to work with: the owner's quote when they have one, otherwise prime
 * plus what comparable SBA loans of this size actually priced at, capped by
 * SBA's published ceiling.
 */
function estimateRate(input: Loan7aInput, loanAmount: number): RateEstimate {
  const type = input.rateType ?? 'variable';
  const basePct = PRIME.pct;
  const ceilingPct = rateCeilingPct(loanAmount, basePct);

  if (input.ratePct !== undefined) {
    if (!Number.isFinite(input.ratePct) || input.ratePct < 0) {
      throw new Error('ratePct must be zero or more');
    }
    const quoted = round2(input.ratePct);
    return {
      // Everything downstream prices at a rate SBA actually permits.
      pct: Math.min(quoted, ceilingPct),
      quotedPct: quoted,
      basePct,
      baseAsOf: PRIME.asOf,
      spreadPct: round2(Math.min(quoted, ceilingPct) - basePct),
      ceilingPct,
      type,
      isEstimate: false,
      lowPct: null,
      highPct: null,
      aboveCeiling: round2(input.ratePct) > ceilingPct,
      citation: {
        source: 'The rate you were quoted',
        citation: null,
        asOf: todayIso(),
        verified: true,
      },
    };
  }

  const bucket = SPREADS.bySize[sizeBucketFor(loanAmount)] ?? SPREADS.global;
  const cap = (spread: number) => round2(Math.min(basePct + spread, ceilingPct));

  return {
    pct: cap(bucket.p50),
    basePct,
    baseAsOf: PRIME.asOf,
    spreadPct: bucket.p50,
    ceilingPct,
    type,
    isEstimate: true,
    lowPct: cap(bucket.p10),
    highPct: cap(bucket.p90),
    aboveCeiling: false,
    citation: {
      source: `Observed spreads over prime on ${bucket.n.toLocaleString(
        'en-US',
      )} comparable SBA 7(a) loans, with prime from ${PRIME.source}`,
      citation: PRIME.citation,
      asOf: PRIME.asOf,
      verified: true,
    },
  };
}

/**
 * What the payment becomes if prime rises. Most 7(a) loans are variable, and an
 * owner quoted a rate today has usually not been shown this.
 *
 * SBA variable loans re-amortise the payment at each adjustment rather than
 * extending the term, so each row is the payment that would be recalculated on
 * the remaining balance — approximated here by re-amortising the full balance,
 * which is the figure a borrower should budget against.
 */
function rateRiseTable(
  amountBorrowed: number,
  rate: RateEstimate,
  termMonths: number,
  interestOnlyMonths: number,
): RateRiseRow[] {
  // The baseline is the rate actually chargeable today, not a quote that
  // exceeds SBA's ceiling — otherwise "Today" would show a saving.
  const baseEffective = Math.min(rate.pct, rate.ceilingPct);
  const atBase = amortize(amountBorrowed, baseEffective, termMonths, interestOnlyMonths);
  const rows: RateRiseRow[] = [];

  for (const bump of [0, 1, 2, 3]) {
    const ratePct = round2(rate.pct + bump);
    const aboveCeiling = ratePct > rate.ceilingPct;
    const effective = Math.min(ratePct, rate.ceilingPct);
    const a = amortize(amountBorrowed, effective, termMonths, interestOnlyMonths);
    rows.push({
      label: bump === 0 ? 'Today' : `If prime rises ${bump} point${bump > 1 ? 's' : ''}`,
      ratePct: round2(effective),
      monthlyPayment: a.payment,
      extraPerMonth: round2(a.payment - atBase.payment),
      extraOverTerm: round2(a.totalPaid - atBase.totalPaid),
      aboveCeiling,
    });
  }
  return rows;
}

/** The whole estimate. */
export function estimate7a(input: Loan7aInput): Loan7aEstimate {
  const approvalDate = input.approvalDate ?? todayIso();
  const loanType = input.loanType ?? '7a';

  // --- term, from what the money is for. Computed on total project cost,
  // before any equity, which is what Appendix 15 requires.
  const maturity = maxMaturity({
    allocations: input.allocations,
    ...(input.isChangeOfOwnership !== undefined
      ? { isChangeOfOwnership: input.isChangeOfOwnership }
      : {}),
    ...(input.specialPurposeProperty !== undefined
      ? { specialPurposeProperty: input.specialPurposeProperty }
      : {}),
    ...(input.isFarm !== undefined ? { isFarm: input.isFarm } : {}),
    ...(input.equipmentUsefulLifeYears !== undefined
      ? { equipmentUsefulLifeYears: input.equipmentUsefulLifeYears }
      : {}),
    ...(input.installationMonths !== undefined
      ? { installationMonths: input.installationMonths }
      : {}),
    ...(input.constructionMonths !== undefined
      ? { constructionMonths: input.constructionMonths }
      : {}),
  });

  const projectCost = maturity.totalAmount;
  const equityInjection = Math.max(input.equityInjection ?? 0, 0);
  if (equityInjection >= projectCost) {
    throw new Error(
      `Your own money (${MONEY(equityInjection)}) covers the whole project (${MONEY(
        projectCost,
      )}), so there is nothing to borrow.`,
    );
  }
  const loanAmount = round2(projectCost - equityInjection);

  const notes: string[] = [];
  let termMonths = maturity.maxTermMonths;
  if (input.termMonths !== undefined) {
    if (!Number.isInteger(input.termMonths) || input.termMonths <= 0) {
      throw new Error('termMonths must be a positive whole number of months');
    }
    if (input.termMonths > maturity.maxTermMonths) {
      throw new Error(
        `SBA allows at most ${maturity.maxTermYears} years for this use of the money, ` +
          `so a ${Math.round(input.termMonths / 12)}-year term is not available.`,
      );
    }
    termMonths = input.termMonths;
    notes.push(
      `Using the ${Math.round(
        termMonths / 12,
      )}-year term you asked for, shorter than the ${maturity.maxTermYears} years SBA allows here.`,
    );
  }

  const interestOnlyMonths = Math.max(input.interestOnlyMonths ?? 0, 0);
  const rate = estimateRate(input, loanAmount);

  // --- fee and closing costs, which decide what is actually borrowed.
  const closing = cashToClose({
    grossAmount: loanAmount,
    maturityMonths: termMonths,
    loanType,
    approvalDate,
    annualRatePct: rate.pct,
    ...(input.naicsSector !== undefined ? { naicsSector: input.naicsSector } : {}),
    ...(input.isMarc !== undefined ? { isMarc: input.isMarc } : {}),
    ...(input.isVeteranOwned !== undefined ? { isVeteranOwned: input.isVeteranOwned } : {}),
    ...(input.priorWithin90Days !== undefined
      ? { priorWithin90Days: input.priorWithin90Days }
      : {}),
    equityInjection,
    projectCost,
    includesRealEstate: input.allocations.some((a) => a.use === 'real-estate'),
    ...(input.isChangeOfOwnership !== undefined
      ? { isChangeOfOwnership: input.isChangeOfOwnership }
      : {}),
    ...(input.businessPurchasePrice !== undefined
      ? { businessPurchasePrice: input.businessPurchasePrice }
      : {}),
    ...(input.closeRelationshipBuyerSeller !== undefined
      ? { closeRelationshipBuyerSeller: input.closeRelationshipBuyerSeller }
      : {}),
    ...(input.financeGuarantyFee !== undefined
      ? { financeGuarantyFee: input.financeGuarantyFee }
      : {}),
    ...(input.costOverrides !== undefined ? { costOverrides: input.costOverrides } : {}),
  });

  const amountBorrowed = closing.amountBorrowed;
  const schedule = amortize(amountBorrowed, rate.pct, termMonths, interestOnlyMonths);

  const exposure = checkExposure({
    grossAmount: loanAmount,
    loanType,
    ...(input.priorWithin90Days !== undefined
      ? { priorWithin90Days: input.priorWithin90Days }
      : {}),
    ...(input.otherGuaranteed7a !== undefined
      ? { otherGuaranteed7a: input.otherGuaranteed7a }
      : {}),
    ...(input.existing504Guaranteed !== undefined
      ? { existing504Guaranteed: input.existing504Guaranteed }
      : {}),
  });

  // --- notes, in the order an owner cares about.
  notes.unshift(...maturity.notes);
  if (rate.isEstimate) {
    notes.push(
      `The ${rate.pct}% rate is an estimate from what comparable SBA loans actually priced at — lenders commonly land between ${rate.lowPct}% and ${rate.highPct}%. SBA caps this loan at ${rate.ceilingPct}%. Put your lender's quoted rate in to make every figure here exact.`,
    );
  }
  if (rate.aboveCeiling) {
    notes.push(
      `The ${rate.quotedPct}% you were quoted is above the ${rate.ceilingPct}% maximum SBA allows on a 7(a) loan of ${MONEY(
        loanAmount,
      )}. Figures here use ${rate.ceilingPct}%. Ask your lender about it — either the quote is wrong or this is not a 7(a) loan.`,
    );
  }
  if (rate.type === 'variable') {
    notes.push(
      `Most 7(a) loans are variable, tied to prime (${rate.basePct}% as of ${rate.baseAsOf}), so your payment can move. The table shows what happens if it rises.`,
    );
  }
  if (interestOnlyMonths > 0) {
    notes.push(
      `For the first ${interestOnlyMonths} months you pay interest only, ${MONEY(
        schedule.interestOnlyPayment ?? 0,
      )} a month. After that the payment rises to ${MONEY(
        schedule.payment,
      )}, because the whole balance is repaid over the ${termMonths - interestOnlyMonths} months left.`,
    );
  }
  notes.push(...closing.notes);
  if (!exposure.ok) notes.push(...exposure.violations);

  const totalFees = closing.guarantyFee + closing.closingCostsLow;
  return {
    projectCost,
    equityInjection,
    loanAmount,
    amountBorrowed,
    maturity,
    termMonths,
    termYears: Math.round((termMonths / 12) * 10) / 10,
    rate,
    schedule,
    monthlyPayment: schedule.payment,
    interestOnlyPayment: schedule.interestOnlyPayment,
    closing,
    lenderAnnualFee: closing.lenderAnnualFee,
    totalInterest: schedule.totalInterest,
    totalCost: round2(schedule.totalInterest + totalFees),
    rateRise:
      rate.type === 'variable'
        ? rateRiseTable(amountBorrowed, rate, termMonths, interestOnlyMonths)
        : null,
    exposure,
    notes,
  };
}
